import type { ShouldRevalidateFunction } from "react-router";

/**
 * Revalidation guard for the products layout (`_storefront._products.tsx`).
 *
 * The products layout loader fetches vendor data based on `vendorId` and
 * `deliveryMethod` search params.  It should only revalidate when those
 * specific params change -- not when other product filters, pagination, or
 * child-route navigations occur.
 */
export const productsLayoutShouldRevalidate: ShouldRevalidateFunction = ({
	formAction,
	currentUrl,
	nextUrl,
	defaultShouldRevalidate,
}) => {
	// Never revalidate for cart or favorites mutations, or for the pharmacy
	// filtered-list POST -- a read that must not refresh the product grid.
	if (formAction?.includes("/api/cart")) {
		return false;
	}
	if (formAction?.includes("/api/favorites")) {
		return false;
	}
	if (formAction?.includes("/api/vendors-filtered")) {
		return false;
	}

	// Honour explicit revalidation (e.g. useRevalidator) so that flows like
	// stock-validation checkout receive fresh data. When useRevalidator fires,
	// currentUrl and nextUrl are identical and defaultShouldRevalidate is true.
	if (currentUrl.href === nextUrl.href && defaultShouldRevalidate) {
		return true;
	}

	// The loader only depends on vendorId and deliveryMethod.
	// Skip revalidation when those params haven't changed.
	const currentVendorId = currentUrl.searchParams.get("vendorId");
	const nextVendorId = nextUrl.searchParams.get("vendorId");
	const currentDeliveryMethod = currentUrl.searchParams.get("deliveryMethod");
	const nextDeliveryMethod = nextUrl.searchParams.get("deliveryMethod");

	if (
		currentVendorId === nextVendorId &&
		currentDeliveryMethod === nextDeliveryMethod
	) {
		return false;
	}

	return defaultShouldRevalidate;
};
